import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@shadcn-in-nx/ui/popover";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@shadcn-in-nx/ui/dropdown-menu";
import { ChevronDown, GitBranch, Download } from "lucide-react";
import PublishTabs from "./PublishTabs";
import { usePublishData } from "./usePublishData";
import { useWorkflow } from "../context/WorkflowContext";
import { useChatContext } from "../Chat/ChatContext";
import { useTranslation } from "react-i18next";
import { useGitHubSync } from "./useGitHubSync";

function PublishButton() {
    const { t } = useTranslation();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const containerRef = useRef(null);
    const { workflow, refetchWorkflow } = useWorkflow();
    const { selectedEnvironment, fetchComponents, setAppViewMode, setSelectedRightPanel } = useChatContext();

    const { isPushing, isPulling, handlePush, handlePull } = useGitHubSync(
        workflow, selectedEnvironment, refetchWorkflow, fetchComponents
    );

    const handlePublishSuccess = () => setPopoverOpen(false);
    const data = usePublishData(workflow, popoverOpen, handlePublishSuccess, setPopoverOpen);

    const handleGoToDomains = () => {
        setPopoverOpen(false);
        setAppViewMode("dashboard");
        setSelectedRightPanel("domains");
    };

    const handleGoToUsers = () => {
        setPopoverOpen(false);
        setAppViewMode("dashboard");
        setSelectedRightPanel("users");
    };

    const handleClickOutside = useCallback((e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) setPopoverOpen(false);
    }, []);

    useEffect(() => {
        if (popoverOpen) document.addEventListener("pointerdown", handleClickOutside);
        return () => document.removeEventListener("pointerdown", handleClickOutside);
    }, [popoverOpen, handleClickOutside]);

    if (!workflow) return null;

    const isGitHub = workflow.isGitHubEnabled && workflow.gitHubRepo;
    const currentBranch = selectedEnvironment || "main";

    if (isGitHub) {
        return (
            <div ref={containerRef} className="relative">
                <div className="flex items-center">
                    <Button onClick={handlePush} size="sm" variant="success"
                        disabled={isPushing || isPulling} className="rounded-r-none whitespace-nowrap text-xs px-2">
                        <GitBranch className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="ml-1">{isPushing ? t("appPreview.gitHubPushing") : t("appPreview.gitHubPush") || "Push"}</span>
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="success" className="rounded-l-none border-l border-l-white/20 px-1.5"
                                disabled={isPushing || isPulling}>
                                <ChevronDown className="w-3.5 h-3.5" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handlePull} disabled={isPulling}>
                                <Download className="w-4 h-4 mr-2" />
                                {isPulling ? t("appPreview.gitHubPulling") : t("appPreview.gitHubPullBranch", { branch: currentBranch })}
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => setPopoverOpen(true)}>
                                {t("appPreview.publish")}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                {popoverOpen && (
                    <div className="absolute right-0 top-full mt-2 w-96 z-50 rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95">
                        <PublishTabs data={data} workflow={workflow} onGoToDomains={handleGoToDomains} onGoToUsers={handleGoToUsers} onClose={() => setPopoverOpen(false)} />
                    </div>
                )}
            </div>
        );
    }

    return (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
                <Button size="sm" variant="success">{t("appPreview.publish")}</Button>
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={8} className="w-96 p-0">
                <PublishTabs data={data} workflow={workflow} onGoToDomains={handleGoToDomains} onGoToUsers={handleGoToUsers} onClose={() => setPopoverOpen(false)} />
            </PopoverContent>
        </Popover>
    );
}

export default React.memo(PublishButton);
